
import { isMobileResponsive } from '../base/environment/utils';
import { MHParticipantConnectionStatus } from '../base/lib-meet-hour';
import { MEDIA_TYPE } from '../base/media';
import {
    getLocalParticipant,
    getParticipantById,
    getParticipantCountWithFake,
    getPinnedParticipant
} from '../base/participants';
import { toState } from '../base/redux';
import {
    getLocalVideoTrack,
    getScreenShareTrack,
    getTrackByMediaTypeAndParticipant,
    isLocalTrackMuted,
    isRemoteTrackMuted
} from '../base/tracks/functions';

import {
    ASPECT_RATIO_BREAKPOINT,
    DISPLAY_AVATAR,
    DISPLAY_AVATAR_WITH_NAME,
    DISPLAY_BLACKNESS_WITH_NAME,
    DISPLAY_VIDEO,
    DISPLAY_VIDEO_WITH_NAME,
    SQUARE_TILE_ASPECT_RATIO,
    TILE_ASPECT_RATIO
} from './constants';

declare var interfaceConfig: Object;

// Minimum space to keep between the sides of the tiles and the sides
// of the window.
const TILE_VIEW_SIDE_MARGINS = 20;

/**
 * Returns true if the filmstrip on mobile is visible, false otherwise.
 *
 * NOTE: Filmstrip on web behaves differently to mobile, much simpler, but so
 * function lies here only for the sake of consistency and to avoid flow errors
 * on import.
 *
 * @param {Object | Function} stateful - The Object or Function that can be
 * resolved to a Redux state object with the toState function.
 * @returns {boolean}
 */
export function isFilmstripVisible(stateful: Object | Function) {
    return toState(stateful)['features/filmstrip'].visible;
}

/**
 * Gets the count of visible participants in the current page of the filmstrip.
 *
 * @param {Object | Function} stateful - The Object or Function that can be
 * resolved to a Redux state object with the toState function.
 * @returns {number} - The count of visible participants in the current page.
 */
export function getVisibleParticipantsCount(stateful: Object | Function) {
    return toState(stateful)['features/filmstrip'].visibleParticipantsCount;
}

/**
 * Determines whether the remote video thumbnails should be displayed/visible in
 * the filmstrip.
 *
 * @param {Object} state - The full redux state.
 * @returns {boolean} - If remote video thumbnails should be displayed/visible
 * in the filmstrip, then {@code true}; otherwise, {@code false}.
 */
export function shouldRemoteVideosBeVisible(state: Object) {
    if (state['features/invite'].calleeInfoVisible) {
        return false;
    }

    // Include fake participants to derive how many thumbnails are dispalyed,
    // as it is assumed all participants, including fake, will be displayed
    // in the filmstrip.
    const participantCount = getParticipantCountWithFake(state);
    let pinnedParticipant;

    return Boolean(
    participantCount > 2

      // Always show the filmstrip when there is another participant to
      // show and the  local video is pinned, or the toolbar is displayed.
      || (participantCount > 1
        && (state['features/toolbox'].visible
          || ((pinnedParticipant = getPinnedParticipant(state))
            && pinnedParticipant.local)))
      || state['features/base/config'].disable1On1Mode
    );
}

/**
 * Checks whether there is a playable video stream available for the user associated with the passed ID.
 *
 * @param {Object | Function} stateful - The Object or Function that can be
 * resolved to a Redux state object with the toState function.
 * @param {string} id - The id of the participant.
 * @returns {boolean} <tt>true</tt> if there is a playable video stream available
 * or <tt>false</tt> otherwise.
 */
export function isVideoPlayable(stateful: Object | Function, id: String) {
    const state = toState(stateful);
    const tracks = state['features/base/tracks'];
    const participant = id
        ? getParticipantById(state, id)
        : getLocalParticipant(state);
    const isLocal = participant?.local ?? true;
    const { connectionStatus } = participant || {};
    const screenShareTrack = id
        ? getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.SCREENSHARE, id)
            || getScreenShareTrack(tracks, id)
        : undefined;
    const videoTrack = isLocal
        ? screenShareTrack || getLocalVideoTrack(tracks)
        : screenShareTrack || getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.VIDEO, id);
    const isAudioOnly = Boolean(state['features/base/audio-only'].enabled);
    let isPlayable = false;

    if (isLocal) {
        const isVideoMuted = isLocalTrackMuted(tracks, MEDIA_TYPE.VIDEO);

        isPlayable = Boolean(videoTrack) && !isVideoMuted && !isAudioOnly;
    } else if (!participant?.isFakeParticipant) {
    // remote participants excluding shared video
        const isVideoMuted = isRemoteTrackMuted(tracks, MEDIA_TYPE.VIDEO, id);

        isPlayable
      = Boolean(videoTrack)
      && !isVideoMuted
      && !isAudioOnly
      && connectionStatus === MHParticipantConnectionStatus.ACTIVE;
    }

    return isPlayable;
}

/**
 * Calculates the size for thumbnails when in horizontal view layout.
 *
 * @param {number} clientHeight - The height of the app window.
 * @returns {{local: {height, width}, remote: {height, width}}}
 */
export function calculateThumbnailSizeForHorizontalView(
        clientHeight: number = 0
) {
    const topBottomMargin = 15;
    const availableHeight = Math.min(
    clientHeight,
    (interfaceConfig.FILM_STRIP_MAX_HEIGHT || 120) + topBottomMargin
    );
    const height = availableHeight - topBottomMargin;

    return {
        local: {
            height,
            width: Math.floor(interfaceConfig.LOCAL_THUMBNAIL_RATIO * height)
        },
        remote: {
            height,
            width: Math.floor(interfaceConfig.REMOTE_THUMBNAIL_RATIO * height)
        }
    };
}

/**
 * Calculates the size for thumbnails when in tile view layout.
 *
 * @param {Object} dimensions - The desired dimensions of the tile view grid.
 * @returns {{height, width}}
 */
export function calculateThumbnailSizeForTileView({
    columns,
    visibleRows,
    clientWidth,
    clientHeight,
    disableResponsiveTiles
}: Object) {
    let aspectRatio = TILE_ASPECT_RATIO;

    if (!disableResponsiveTiles && clientWidth < ASPECT_RATIO_BREAKPOINT) {
        aspectRatio = SQUARE_TILE_ASPECT_RATIO;
    }

    const viewWidth = clientWidth - TILE_VIEW_SIDE_MARGINS;
    const viewHeight = clientHeight - TILE_VIEW_SIDE_MARGINS;
    const initialWidth = viewWidth / columns;
    const aspectRatioHeight = initialWidth / aspectRatio;
    const height = Math.floor(
    Math.min(aspectRatioHeight, viewHeight / visibleRows)
    );
    const width = Math.floor(aspectRatio * height);

    return {
        height,
        width
    };
}

/**
 * Returns the width of the visible area (doesn't include the left margin/padding) of the the vertical filmstrip.
 *
 * @returns {number} - The width of the vertical filmstrip.
 */
export function getVerticalFilmstripVisibleAreaWidth() {
    // Adding 11px for the 2px right margin, 2px borders on the left and right and 5px right padding.
    // Also adding 7px for the scrollbar. Note that we are not counting the left margins and paddings because this
    // function is used for calculating the available space and they are invisible.
    // TODO: Check if we can remove the left margins and paddings from the CSS.
    // FIXME: This function is used to calculate the size of the large video, etherpad or shared video. Once everything
    // is reactified this calculation will need to move to the corresponding components.
    const filmstripMaxWidth = (interfaceConfig.FILM_STRIP_MAX_HEIGHT || 120) + 18;

    return Math.min(filmstripMaxWidth, window.innerWidth);
}

/**
 * Computes information that determine the display mode.
 *
 * @param {Object} input - Obejct containing all necessary information for determining the display mode for
 * the thumbnail.
 * @returns {number} - One of <tt>DISPLAY_VIDEO</tt>, <tt>DISPLAY_AVATAR</tt> or <tt>DISPLAY_BLACKNESS_WITH_NAME</tt>.
 */
export function computeDisplayMode(input: Object) {
    const {
        isAudioOnly,
        isCurrentlyOnLargeVideo,
        isScreenSharing,
        canPlayEventReceived,
        isHovered,
        isRemoteParticipant,
        tileViewActive
    } = input;
    const adjustedIsVideoPlayable
    = input.isVideoPlayable && (!isRemoteParticipant || canPlayEventReceived);

    if (!tileViewActive && isScreenSharing && isRemoteParticipant) {
        return isHovered ? DISPLAY_AVATAR_WITH_NAME : DISPLAY_AVATAR;
    } else if (isCurrentlyOnLargeVideo && !tileViewActive) {
    // Display name is always and only displayed when user is on the stage
        return adjustedIsVideoPlayable && !isAudioOnly
            ? DISPLAY_BLACKNESS_WITH_NAME
            : DISPLAY_AVATAR_WITH_NAME;
    } else if (adjustedIsVideoPlayable && !isAudioOnly) {
    // check hovering and change state to video with name
        return isHovered ? DISPLAY_VIDEO_WITH_NAME : DISPLAY_VIDEO;
    }

    // check hovering and change state to avatar with name
    return isHovered ? DISPLAY_AVATAR_WITH_NAME : DISPLAY_AVATAR;
}

/**
 * Calculates the Accruate Height and Width of Thumbnail in Tile View.
 *
 * @returns {object} - Height and Width of the Thumbnail in TileView According to the New UI
 */
export function calculateTileViewThumbnailDimensions(state) {
    const participantsLength = getParticipantCountWithFake(state);
    const _isMobile = isMobileResponsive();
    const { conferenceView } = state['features/base/conference'];
    const margins = conferenceView === 'float' ? '90px' : '165px';

    let height;
    let width;

    if (!_isMobile) {
        if (participantsLength === 2 || participantsLength === 3) {
            height = `calc(100vh - ${margins})`;
            width = '100%';
        } else if (participantsLength >= 4 && participantsLength <= 10) {
            height = `calc((100vh - ${margins})/2)`;
            width = '100%';
        } else if (participantsLength > 10 && participantsLength <= 21) {
            height = `calc((100vh - ${margins})/3)`;
            width = '100%';
        } else {
            height = `calc((100vh - ${margins})/4)`;
            width = '100%';
        }
    }

    if (_isMobile) {
        switch (participantsLength) {
        case 2:
            height = `calc((100vh - ${margins})/2)`;
            width = '100%';
            break;
        case 3:
            height = `calc((100vh - ${margins})/2)`;
            width = '100%';
            break;
        case 4:
            height = `calc((100vh - ${margins})/2)`;
            width = '100%';
            break;
        default:
            height = `calc((100vh - ${margins})/3)`;
            width = '100%';
            break;
        }
    }

    return { height,
        width };
}

/**
 * Selects a Participant to have max grid view in Tile View.
 *
 * @returns {object} - Participant to have maximun height or width in TileView According to the New UI
 */
export function selectParticipantforLargeViewGrid(state) {
    const participants = state['features/base/participants'];
    const config = state['features/base/config'];
    const jwt = state['features/base/jwt'];

    // Find key participant types
    const localParticipant = participants.find(p => p.local);
    const isLocalModerator = localParticipant && jwt?.user?.moderator;
    const moderators = participants.filter(p => p.role === 'moderator');
    const pinnedByModerator = participants.find(p => p.moderatorPin === true);
    const { iAmRecorder } = config;

    if (pinnedByModerator) {
        return pinnedByModerator.id;
    }

    if (!iAmRecorder && (pinnedByModerator?.local || isLocalModerator || moderators[0]?.local)) {
        return 'localVideoTileViewContainer';
    }

    if (isLocalModerator && !iAmRecorder) {
        return localParticipant.id;
    }

    if (moderators.length > 0) {
        return moderators[0].id;
    }

    return participants[0]?.id;
}
